// Google Analytics (gtag) utilities for frontend tracking
// Note: Using GTM dataLayer for event tracking

declare global {
  interface Window {
    gtag?: any;
    dataLayer?: any[];
  }
}

/**
 * Check if we're in a browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Extract GA4 client_id from _ga cookie
 * Format: GA1.1.{random1}.{random2} -> we want {random1}.{random2}
 */
export function getGA4ClientId(): string | null {
  if (!isBrowser()) return null;

  const match = document.cookie.match(/_ga=([^;]+)/);
  if (!match) return null;

  const gaCookie = decodeURIComponent(match[1]);
  // Format: GA1.1.123456789.987654321
  const parts = gaCookie.split('.');
  if (parts.length >= 4) {
    // Return the client_id part: "123456789.987654321"
    return `${parts[2]}.${parts[3]}`;
  }

  return null;
}

/**
 * Get GA4 tracking data from browser
 */
export function getGA4TrackingData() {
  return {
    client_id: getGA4ClientId(),
    // GA4 session_id can be extracted from gtag if needed, but client_id is most important
  };
}

/**
 * Push event to GTM dataLayer (works with or without gtag)
 */
function trackEvent(event: string, params: any) {
  if (!isBrowser()) return;

  // Try gtag first (if GA4 is loaded directly)
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', event, params);
  }

  // Fall back to dataLayer (GTM)
  if (window.dataLayer) {
    console.log('Pushing to dataLayer:', event, params);
    window.dataLayer.push({
      event,
      ...params
    });
  }
}

/**
 * Track begin_checkout event for GA4
 * This should be called when user initiates checkout
 */
export function trackBeginCheckout(params: {
  value: number;
  currency: string;
  items: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>;
  coupon?: string;
  promotion_id?: string;
}) {
  trackEvent('begin_checkout', {
    value: params.value,
    currency: params.currency,
    items: params.items,
    ...(params.coupon && { coupon: params.coupon }),
    ...(params.promotion_id && { promotion_id: params.promotion_id }),
  });
}

/**
 * Track purchase event for GA4
 * This should be called on the success page after purchase completion
 */
export function trackPurchase(params: {
  transaction_id: string;
  value: number;
  currency: string;
  items: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>;
  coupon?: string;
  promotion_id?: string;
}) {
  // Track for GA4 (uses 'purchase' event)
  trackEvent('purchase', {
    transaction_id: params.transaction_id,
    value: params.value,
    currency: params.currency,
    items: params.items,
    ...(params.coupon && { coupon: params.coupon }),
    ...(params.promotion_id && { promotion_id: params.promotion_id }),
  });

  // Also track for Google Ads (uses 'conversion' event)
  // This ensures Google Ads conversion tracking gets the data
  trackEvent('conversion', {
    transaction_id: params.transaction_id,
    value: params.value,
    currency: params.currency,
    ...(params.coupon && { coupon: params.coupon }),
    ...(params.promotion_id && { promotion_id: params.promotion_id }),
  });
}

